import React, { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Col, Divider, Flex, Row, Select, Typography } from 'antd';
import { Text } from 'theme/typography';
import { COLORS, TYPOGRAPHY_SIZES } from 'theme';
import { FilterWrapper } from '../SegmentationLabels/styled';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';
import AdvancedFilters from '../molecules/advancedFilters';
import ActiveFilters from '../molecules/activeFilters';
import { useKeypointFilterState } from 'hooks/useFilterState';

interface IProps {
  primaryOptions: AdvancedFilterValue[];
  secondaryOptions: AdvancedFilterValue[];
  advancedFiltersOptions: AdvancedFiltersOptions;
  handlePrimaryOptChange: (e: AdvancedFilterValue) => void;
  onHandleClear: () => void;
  onHandleApply: () => void;
  isLoadingSegmentationClasses?: boolean;
  isSegmentationClassesError?: boolean;
  clearSegmentations?: () => void;
}

const KeypointFilter: FC<IProps> = props => {
  const {
    primaryOptions,
    secondaryOptions,
    advancedFiltersOptions,
    handlePrimaryOptChange,
    onHandleClear,
    onHandleApply,
    isLoadingSegmentationClasses = false,
    isSegmentationClassesError = false,
    clearSegmentations,
  } = props;

  const kp = useKeypointFilterState();
  const { filterState, updateFilterTags } = kp;

  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingRemove, setPendingRemove] = useState<AdvancedFilterValue[]>([]);
  const [pendingApply, setPendingApply] = useState(false);
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(
    () => filterState.advancedFilters
  );
  const [pendingView, setPendingView] = useState(() => filterState.view);
  const [pendingFeatures, setPendingFeatures] = useState<AdvancedFilterValue[]>(
    () => filterState.features
  );
  const initializedFeaturesFromUrlRef = useRef(false);

  useEffect(() => {
    setPendingView(filterState.view);
  }, [filterState.view]);

  useEffect(() => {
    setPendingAdvancedFilters(filterState.advancedFilters);
  }, [filterState.advancedFilters]);

  const updateQueryParams = () => {
    const params: Record<string, string> = {};

    if (filterState.view?.name) {
      params['view'] = filterState.view.name;
    }
    if (filterState.features.length) {
      params['features'] = filterState.features.map((f: AdvancedFilterValue) => f.name).join(',');
    }

    // Handle datasource (single select)
    if (filterState.advancedFilters.datasources && filterState.advancedFilters.datasources.name) {
      params['datasources'] = filterState.advancedFilters.datasources.name;
    }

    // Handle dicomICID (string)
    if (filterState.advancedFilters.dicomICID && filterState.advancedFilters.dicomICID.trim()) {
      params['dicomICID'] = filterState.advancedFilters.dicomICID;
    }

    // Handle study (string)
    if (filterState.advancedFilters.study && filterState.advancedFilters.study.trim()) {
      params['study'] = filterState.advancedFilters.study;
    }

    if (
      filterState.advancedFilters.keypointICID &&
      filterState.advancedFilters.keypointICID.trim()
    ) {
      params['keypointICID'] = filterState.advancedFilters.keypointICID;
    }

    // Annotation set name
    if (
      filterState.advancedFilters.annotationSetName &&
      typeof filterState.advancedFilters.annotationSetName === 'string' &&
      filterState.advancedFilters.annotationSetName.trim()
    ) {
      params['annotation_set_name'] = filterState.advancedFilters.annotationSetName;
    }

    if (filterState.advancedFilters.technician && filterState.advancedFilters.technician.trim()) {
      params['technician'] = filterState.advancedFilters.technician;
    }

    if (filterState.advancedFilters.created_at_after) {
      params['created_at_after'] = filterState.advancedFilters.created_at_after;
    }
    if (filterState.advancedFilters.created_at_before) {
      params['created_at_before'] = filterState.advancedFilters.created_at_before;
    }
    if (filterState.advancedFilters.updated_at_after) {
      params['updated_at_after'] = filterState.advancedFilters.updated_at_after;
    }
    if (filterState.advancedFilters.updated_at_before) {
      params['updated_at_before'] = filterState.advancedFilters.updated_at_before;
    }
    // Frame ID and review status
    if ((filterState.advancedFilters as any).frameId)
      params['frame_id'] = (filterState.advancedFilters as any).frameId;
    if (
      (filterState.advancedFilters as any).reviewStatus &&
      Array.isArray((filterState.advancedFilters as any).reviewStatus) &&
      (filterState.advancedFilters as any).reviewStatus.length > 0
    )
      params['review_status'] = (filterState.advancedFilters as any).reviewStatus.join(',');

    setSearchParams(params);
  };

  useEffect(() => {
    if (!primaryOptions.length) return;

    const viewParam = searchParams.get('view');
    if (viewParam) {
      const foundView =
        primaryOptions.find(opt => opt.name === viewParam) ||
        primaryOptions.find(opt => opt.uuid === viewParam);
      if (foundView) {
        kp.setView(foundView);
        setPendingView(foundView);
      } else {
        kp.setView(undefined);
        setPendingView(undefined);
      }
    }
  }, [searchParams, primaryOptions]);

  useEffect(() => {
    if (!secondaryOptions.length) return;
    const features = searchParams.get('features');
    if (features) {
      const featureParams = features
        .split(',')
        .map(f => decodeURIComponent(f))
        .map(f => f.replace(/\+/g, ' '));
      const selectedFeatures = secondaryOptions.filter(
        opt => featureParams.includes(opt.name) || featureParams.includes(opt.uuid)
      );
      setPendingFeatures(selectedFeatures);
      // Auto-apply features from URL only once on initial load
      if (!initializedFeaturesFromUrlRef.current) {
        kp.setFeatures(selectedFeatures);
        initializedFeaturesFromUrlRef.current = true;
      }
    } else {
      setPendingFeatures([]);
      kp.setFeatures([]);
    }
  }, [searchParams, secondaryOptions]);

  useEffect(() => {
    // --- Datasource: map name or uuid to object (single-select) ---
    const datasourceParam = searchParams.get('datasources');
    if (datasourceParam && advancedFiltersOptions.datasources) {
      const foundDatasource = (advancedFiltersOptions.datasources as AdvancedFilterValue[]).find(
        opt => opt.name === datasourceParam || opt.uuid === datasourceParam
      );
      kp.setAdvancedFilter('datasources', foundDatasource || undefined);
    } else {
      kp.setAdvancedFilter('datasources', undefined);
    }

    // --- DicomICID: string ---
    const dicomICIDParam = searchParams.get('dicomICID');
    if (dicomICIDParam) {
      kp.setAdvancedFilter('dicomICID', dicomICIDParam);
    } else {
      kp.setAdvancedFilter('dicomICID', '');
    }

    // --- Study: string ---
    const studyParam = searchParams.get('study');
    kp.setAdvancedFilter('study', studyParam || '');

    const keypointICIDParam = searchParams.get('keypointICID');
    if (keypointICIDParam) {
      kp.setAdvancedFilter('keypointICID', keypointICIDParam);
    } else {
      kp.setAdvancedFilter('keypointICID', '');
    }

    const annotationSetParam = searchParams.get('annotation_set_name');
    if (annotationSetParam) {
      kp.setAdvancedFilter('annotationSetName', annotationSetParam);
    } else {
      kp.setAdvancedFilter('annotationSetName', '');
    }

    const technicianParam = searchParams.get('technician');
    if (technicianParam) {
      kp.setAdvancedFilter('technician', technicianParam);
    } else {
      kp.setAdvancedFilter('technician', '');
    }

    const createdAtAfterParam = searchParams.get('created_at_after');
    kp.setAdvancedFilter('created_at_after', createdAtAfterParam || undefined);

    const createdAtBeforeParam = searchParams.get('created_at_before');
    kp.setAdvancedFilter('created_at_before', createdAtBeforeParam || undefined);

    const updatedAtAfterParam = searchParams.get('updated_at_after');
    kp.setAdvancedFilter('updated_at_after', updatedAtAfterParam || undefined);

    const updatedAtBeforeParam = searchParams.get('updated_at_before');
    kp.setAdvancedFilter('updated_at_before', updatedAtBeforeParam || undefined);
    // --- Frame ID(s): support comma-separated list or single value ---
    const frameIdParam = searchParams.get('frame_id');
    if (frameIdParam) {
      kp.setAdvancedFilter('frameId', decodeURIComponent(frameIdParam));
    } else {
      kp.setAdvancedFilter('frameId', '');
    }
    // --- Review status: comma-separated list -> array of statuses ---
    const reviewStatusParam = searchParams.get('review_status');
    if (reviewStatusParam) {
      const statuses = decodeURIComponent(reviewStatusParam)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      kp.setAdvancedFilter('reviewStatus', statuses);
    } else {
      kp.setAdvancedFilter('reviewStatus', []);
    }
  }, [searchParams, primaryOptions, advancedFiltersOptions]);

  useEffect(() => {
    // Only run if there are query params or filterState has actual values set
    // This useEffect is to trigger updateFilterTags, which then prepares tags for display
    const hasActiveFilters =
      filterState.view ||
      filterState.features.length > 0 ||
      Object.values(filterState.advancedFilters).some(
        val =>
          (Array.isArray(val) && val.length > 0) ||
          (typeof val === 'object' && val !== null && 'name' in val) ||
          (typeof val === 'string' && val.trim() !== '')
      );

    if (hasActiveFilters || Array.from(searchParams.keys()).length > 0) {
      updateFilterTags();
    }
  }, [filterState.view, filterState.features, filterState.advancedFilters, searchParams]);

  const handlePrimaryChange = (_: string, option: any) => {
    const { uuid, name } = option;
    setPendingView({ uuid, name });
    handlePrimaryOptChange({ uuid, name });
  };

  const handleSecondaryChange = (_: string[], options: any) => {
    const selectedFeatures = options.map((option: { uuid: any; name: any }) => ({
      uuid: option.uuid,
      name: option.name,
    }));
    setPendingFeatures(selectedFeatures);
  };

  const handleAdvancedFiltersChange = (
    key: string,
    value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
  ) => {
    setPendingAdvancedFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    kp.resetFilters();
    onHandleClear();
    setSearchParams({});
    setPendingView(undefined);
    setPendingFeatures([]);
    setPendingAdvancedFilters({
      datasources: undefined,
      dicomICID: undefined,
      study: undefined,
      keypointICID: undefined,
      technician: [],
      created_at_after: undefined,
      created_at_before: undefined,
      updated_at_after: undefined,
      updated_at_before: undefined,
    });
    if (clearSegmentations) clearSegmentations();
  };
  const handlePendingRemovals = () => {
    pendingRemove.forEach(tag => {
      // Remove from the actual Redux filter state
      kp.removeTag(tag, false);

      // Also clear any pending selections so Apply won't reintroduce them
      const tagName = tag.name || '';
      const [rawKey] = tagName.split(':');
      const key = rawKey?.trim();

      if (key?.toLowerCase() === 'view') {
        setPendingView(undefined);
      } else if (key?.toLowerCase() === 'feature') {
        // Keep pendingFeatures in sync when a Feature tag is removed
        setPendingFeatures(prev => prev.filter(f => f.uuid !== tag.uuid));
      } else if (key) {
        setPendingAdvancedFilters((prev: any) => {
          const next = { ...prev } as any;
          const currentVal = (next as any)[key];

          if (Array.isArray(currentVal)) {
            (next as any)[key] = currentVal.filter((item: any) => item?.uuid !== tag.uuid);
          } else if (currentVal && typeof currentVal === 'object') {
            if (currentVal.uuid === tag.uuid) {
              (next as any)[key] = undefined; // Changed from empty object to undefined
            }
          } else if (typeof currentVal === 'string') {
            (next as any)[key] = undefined; // Changed from empty string to undefined
          }
          return next;
        });
      }
    });
    setPendingRemove([]);
  };

  const handleTagPendingRemove = (tag: AdvancedFilterValue) => {
    const tagName = tag.name || '';
    const [rawKey] = tagName.split(':');
    const key = rawKey?.trim();

    if (key?.toLowerCase() === 'view') {
      setPendingView(undefined);
    } else if (key?.toLowerCase() === 'feature') {
      setPendingFeatures(prev => prev.filter(f => f.uuid !== tag.uuid));
    } else if (key) {
      setPendingAdvancedFilters((prev: any) => {
        const next = { ...prev } as any;
        const currentVal = (next as any)[key];

        if (Array.isArray(currentVal)) {
          (next as any)[key] = currentVal.filter((item: any) => item?.uuid !== tag.uuid);
        } else if (currentVal && typeof currentVal === 'object') {
          if (currentVal.uuid === tag.uuid) {
            (next as any)[key] = undefined; // Changed from empty object to undefined
          }
        } else if (typeof currentVal === 'string') {
          (next as any)[key] = undefined; // Changed from empty string to undefined
        }
        return next;
      });
    }
    // Immediately remove from Redux filter state so UI updates instantly,
    // then update the URL param so browser stays in sync.
    // This makes tag removal behavior consistent with other pages.
    try {
      kp.removeTag(tag, false);
    } catch (err) {
      // ignore
    }
    // update URL param immediately so UI and URL stay in sync
    try {
      const paramMap: { [k: string]: string } = {
        view: 'view',
        features: 'features',
        datasources: 'datasources',
        dicomicid: 'dicomICID',
        study: 'study',
        keypointicid: 'keypointICID',
        technician: 'technician',
        annotationSetName: 'annotation_set_name',
        created_at_after: 'created_at_after',
        created_at_before: 'created_at_before',
        updated_at_after: 'updated_at_after',
        updated_at_before: 'updated_at_before',
        frameid: 'frame_id',
        reviewstatus: 'review_status',
      };

      const rawKeyLower = (rawKey || '').trim().toLowerCase();
      const paramName = paramMap[rawKeyLower];
      if (paramName) {
        const val = tagName.split(':').slice(1).join(':').trim();
        const newParams = new URLSearchParams(searchParams);
        const existing = newParams.get(paramName);
        if (existing) {
          const partsRaw = existing.split(',');
          const parts = partsRaw.map(p => decodeURIComponent(p).trim()).filter(Boolean);
          // Support tags that represent multiple comma-separated values (e.g. dicomICID lists)
          const decodedVal = decodeURIComponent(val);
          let filtered: string[] = [];
          if (decodedVal.includes(',')) {
            const removePartsRaw = decodedVal.split(',');
            const removeParts = removePartsRaw.map(p => p.trim()).filter(Boolean);
            filtered = parts.filter(p => !removeParts.includes(p));
          } else {
            filtered = parts.filter(p => p !== decodedVal);
          }
          if (filtered.length === 0) {
            newParams.delete(paramName);
          } else {
            newParams.set(paramName, filtered.join(','));
          }
        }
        setSearchParams(newParams);
      }
    } catch (err) {
      // ignore
    }
    // Refresh active tags in store based on the updated filterState
    try {
      updateFilterTags();
    } catch (err) {
      // ignore
    }
  };
  const handleApply = () => {
    handlePendingRemovals();
    if (pendingView) kp.setView(pendingView);
    else kp.setView(undefined);
    kp.setFeatures(pendingFeatures);
    (Object.entries(pendingAdvancedFilters) as [string, any][]).forEach(([key, value]) => {
      kp.setAdvancedFilter(key, value as any);
    });
    setPendingApply(true);
  };

  const isObjectFilterEqual = (a: any, b: any) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.uuid === b.uuid && a.name === b.name;
  };

  const areFeaturesEqual = (a: AdvancedFilterValue[], b: AdvancedFilterValue[]) => {
    if (a.length !== b.length) return false;
    const aIds = a.map(f => f.uuid).sort();
    const bIds = b.map(f => f.uuid).sort();
    return aIds.every((id, idx) => id === bIds[idx]);
  };

  const areAdvancedFiltersEqual = (a: any, b: any) => {
    const keys = [
      'datasources',
      'dicomICID',
      'study',
      'keypointICID',
      'technician',
      'created_at_after',
      'created_at_before',
      'updated_at_after',
      'updated_at_before',
    ];
    for (const key of keys) {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        if ((av || '') !== (bv || '')) return false;
      } else {
        if (!isObjectFilterEqual(av, bv)) return false;
      }
    }
    return true;
  };

  const isFilterStateSyncedWithPending = () => {
    const syncedView = isObjectFilterEqual(filterState.view, pendingView);
    const syncedFeatures = areFeaturesEqual(filterState.features, pendingFeatures);
    const syncedAdv = areAdvancedFiltersEqual(filterState.advancedFilters, pendingAdvancedFilters);
    return syncedView && syncedFeatures && syncedAdv;
  };

  useEffect(() => {
    if (!pendingApply) return;
    if (!isFilterStateSyncedWithPending()) return;
    updateQueryParams();
    updateFilterTags();
    onHandleApply();
    setPendingApply(false);
  }, [filterState, pendingApply]);

  const onSelectAll = () => {
    const allFeatures = secondaryOptions.map(option => ({ uuid: option.uuid, name: option.name }));
    setPendingFeatures(allFeatures);
  };

  return (
    <FilterWrapper>
      <Card>
        <Flex vertical gap="middle">
          <Row gutter={[16, 16]} style={{ width: '100%' }} align="bottom">
            <Col xs={24} md={12} xl={6}>
              <Flex vertical>
                <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={600}>
                  View
                </Text>
                <Select
                  showSearch
                  placeholder="Choose a view"
                  optionFilterProp="label"
                  onChange={handlePrimaryChange}
                  fieldNames={{
                    label: 'name',
                    value: 'uuid',
                  }}
                  filterOption={(input, option) =>
                    (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={primaryOptions}
                  value={pendingView?.uuid}
                />
              </Flex>
            </Col>
            <Col xs={24} md={12} xl={14}>
              <Flex vertical>
                <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={600}>
                  Feature
                </Text>
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  mode="multiple"
                  value={pendingFeatures.map((feature: AdvancedFilterValue) => feature.uuid)}
                  allowClear
                  style={{ width: '100%' }}
                  fieldNames={{
                    label: 'name',
                    value: 'uuid',
                  }}
                  placeholder={
                    isLoadingSegmentationClasses ? 'Loading features...' : 'Choose a feature'
                  }
                  options={secondaryOptions}
                  onChange={handleSecondaryChange}
                  loading={isLoadingSegmentationClasses}
                  disabled={isLoadingSegmentationClasses || isSegmentationClassesError}
                  dropdownRender={menu =>
                    isSegmentationClassesError ? (
                      <div style={{ padding: '12px', textAlign: 'center' }}>
                        <Typography.Text type="danger">
                          Failed to load features. Please try selecting a view again.
                        </Typography.Text>
                      </div>
                    ) : secondaryOptions.length > 1 ? (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Flex style={{ padding: '0 8px 4px', width: '100%' }} justify="end">
                          <Button type="link" onClick={onSelectAll}>
                            Select All
                          </Button>
                        </Flex>
                      </>
                    ) : (
                      <>{menu}</>
                    )
                  }
                  notFoundContent={
                    isLoadingSegmentationClasses ? (
                      'Loading features...'
                    ) : isSegmentationClassesError ? (
                      <Typography.Text type="danger">Failed to load features</Typography.Text>
                    ) : (
                      'No features found'
                    )
                  }
                />
              </Flex>
            </Col>
            <Col xs={24} md={24} xl={4}>
              <AdvancedFilters
                advancedFiltersOptions={advancedFiltersOptions}
                handleAdvancedFiltersOptChange={handleAdvancedFiltersChange}
                advancedFilters={pendingAdvancedFilters as any}
                isKeypointMode={true}
              />
            </Col>
          </Row>

          <ActiveFilters
            pendingRemove={pendingRemove}
            setPendingRemove={setPendingRemove}
            keypointMode={true}
            onTagPendingRemove={handleTagPendingRemove}
          />

          <Flex gap={8} justify="end">
            <Button onClick={handleClear}>Clear</Button>
            <Button onClick={handleApply} type="primary">
              Apply Filters
            </Button>
          </Flex>
        </Flex>
      </Card>
    </FilterWrapper>
  );
};

export default KeypointFilter;
